import { useRef, useState } from 'react';
import { ApiError, api, type LoginResult } from '../api';
import { Button, IconEye, IconEyeOff, IconGlobe, IconShield } from '../components';
import { LANGUAGES, errorMessage, type Copy, type Language } from '../i18n';

export function LoginScreen({
  copy,
  language,
  onLanguageChange,
  onSignedIn,
  compact = false,
  heading,
  note,
}: {
  copy: Copy;
  language: Language;
  onLanguageChange: (language: Language) => void;
  onSignedIn: (result: LoginResult) => void;
  /** Inline variant used by the re-login sheet, where the brand block is redundant. */
  compact?: boolean;
  heading?: string;
  note?: string;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A ref flips synchronously; `busy` alone would still let a double-tap through
  // because the disabled attribute is not applied until the next render.
  const inFlight = useRef(false);

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (inFlight.current) return;
    if (!username.trim() || !password) {
      setError(copy.errInvalidCredentials);
      return;
    }

    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await api.login(username.trim(), password);
      if (result.user.role !== 'SUPERVISOR') {
        // A management account signing into the supervisor app is a mistake worth
        // naming rather than a blank screen after a "successful" login.
        setError(copy.errForbidden);
        return;
      }
      setPassword('');
      onSignedIn(result);
    } catch (caught) {
      setError(errorMessage(copy, caught instanceof ApiError ? caught.code : 'NETWORK'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const form = (
    <form className="login-form" onSubmit={submit} noValidate>
      <div className="field">
        <label className="field-label" htmlFor="username">
          {copy.username}
        </label>
        <input
          id="username"
          className="input"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          placeholder={copy.usernamePlaceholder}
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="next"
          aria-invalid={error ? true : undefined}
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="password">
          {copy.password}
        </label>
        <div className="input-affix">
          <input
            id="password"
            className="input"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={copy.passwordPlaceholder}
            autoComplete="current-password"
            enterKeyHint="go"
            aria-invalid={error ? true : undefined}
          />
          <button
            type="button"
            className="input-affix-btn"
            onClick={() => setShowPassword((value) => !value)}
            aria-label={showPassword ? copy.hidePassword : copy.showPassword}
          >
            {showPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
          </button>
        </div>
      </div>

      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}

      <Button type="submit" variant="accent" size="lg" block loading={busy}>
        {busy ? copy.signingIn : copy.signIn}
      </Button>
    </form>
  );

  if (compact) {
    return (
      <div className="login-compact">
        {note && <p className="login-note">{note}</p>}
        {form}
      </div>
    );
  }

  return (
    <main className="login-screen app-frame">
      <div className="login-card">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <IconShield size={26} />
          </span>
          <div>
            <h1 className="brand-name">{copy.appName}</h1>
            <p className="brand-tagline">{copy.tagline}</p>
          </div>
        </div>

        <div className="login-head">
          <h2 className="login-title">{heading ?? copy.signIn}</h2>
          <p className="login-sub">{copy.signInSubtitle}</p>
        </div>

        {form}

        <div className="lang-row">
          <span className="lang-label">
            <IconGlobe size={16} />
            {copy.language}
          </span>
          <div className="lang-switch" role="group" aria-label={copy.language}>
            {LANGUAGES.map((option) => (
              <button
                key={option.code}
                type="button"
                className={`lang-option${option.code === language ? ' is-active' : ''}`}
                onClick={() => onLanguageChange(option.code)}
                aria-pressed={option.code === language}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
